import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface ChatMessage {
  id: string;
  message: string;
  created_at: string;
  is_from_client: boolean;
  client_id: number;
  user_id: string;
}

interface Client {
  id: number;
  name: string;
  email?: string;
  status?: string;
  phone?: string;
  last_contact?: string;
}

interface AdminChatListProps {
  onClientSelect?: (client: Client | null) => void;
}

export const AdminChatList = ({ onClientSelect }: AdminChatListProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!selectedClient) return;
      
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, email, status, phone, last_contact')
        .eq('id', selectedClient)
        .single();

      if (error) {
        console.error('Error fetching client details:', error);
        return;
      }

      setClientDetails(data);
      onClientSelect?.(data);
    };

    fetchClientDetails();
  }, [selectedClient, onClientSelect]);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedClient) return;
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('client_id', selectedClient)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load chat messages"
        });
        return;
      }

      console.log('Fetched messages:', data);
      setMessages(data || []);
    };

    fetchMessages();

    // Subscribe to new messages for this client
    const channel = supabase
      .channel(`chat_messages_${selectedClient}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: `client_id=eq.${selectedClient}`
        },
        (payload) => {
          console.log('Real-time update received:', payload);
          if (payload.eventType === 'INSERT') {
            setMessages(current => [...current, payload.new as ChatMessage]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedClient, toast]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedClient || !user) return;

    const messageData = {
      message: newMessage.trim(),
      client_id: selectedClient,
      is_from_client: false,
      user_id: user.id
    };

    console.log('Sending message:', messageData);

    const { error } = await supabase
      .from('chat_messages')
      .insert([messageData]);

    if (error) {
      console.error('Error sending message:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send message"
      });
      return;
    }

    setNewMessage("");
  };

  // Update the selected client when the component mounts
  useEffect(() => {
    const fetchCurrentClient = async () => {
      const { data: clients, error } = await supabase
        .from('clients')
        .select('*')
        .order('last_contact', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching clients:', error);
        return;
      }

      if (clients && clients.length > 0) {
        setSelectedClient(clients[0].id);
      }
    };

    fetchCurrentClient();
  }, []);

  return (
    <div className="flex flex-col h-[600px] bg-[#1A1F2C] rounded-lg border border-gray-700">
      <div className="p-4 border-b border-gray-700">
        <h2 className="text-xl font-semibold">
          <span className="text-[#8B5CF6]">
            {clientDetails?.name ? `Chat with ${clientDetails.name}` : 'Select a Client'}
          </span>
          {clientDetails?.email && (
            <span className="block text-sm text-gray-400 mt-1">{clientDetails.email}</span>
          )}
        </h2>
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.is_from_client ? "justify-start" : "justify-end"
              }`}
            >
              <div
                className={`max-w-[80%] p-3 rounded-lg ${
                  message.is_from_client
                    ? "bg-gray-700 text-white"
                    : "bg-primary text-primary-foreground"
                }`}
              >
                <p className="text-sm">{message.message}</p>
                <span className="text-xs opacity-70">
                  {new Date(message.created_at).toLocaleTimeString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-gray-800 border-gray-600 text-white"
          />
          <Button type="submit" size="icon">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};